/**
 * Which shops an account may switch between on the MobieTrain Insights tab.
 *
 * The rule is the account's scope, not its title: a general manager has one shop and gets
 * no selector; a district manager with a selection of shops gets a selector; a company-wide
 * role (operations manager, platform admin) gets every shop. The store **code** is what the
 * per-shop embed links are keyed by, so it is derived from the store number when the
 * `/auth/stores` catalogue has not loaded yet.
 */
import type { StoreOption } from '../api/auth'
import type { AuthUser } from '../types/identity'

export interface InsightShop {
  id: number
  /** Three-digit display code, e.g. `022`. */
  code: string
  name: string | null
}

/** Three-digit code for a store number (`22` → `"022"`, `3` → `"003"`). */
export function storeCodeFromId(id: number | null | undefined): string {
  if (id === null || id === undefined || !Number.isFinite(id)) return ''
  const rounded = Math.trunc(id)
  if (rounded < 0 || rounded > 999) return ''
  return String(rounded).padStart(3, '0')
}

/** Roles whose scope is the whole company (mirrors `core/roles.py`). */
const COMPANY_WIDE_ROLES = new Set(['operations_manager', 'system_admin'])

/**
 * The shops in scope, in catalogue order.
 *
 * - explicit `scope_store_ids` (district managers) → exactly those shops;
 * - company-wide roles / platform admins → every shop;
 * - everybody else → their own single store.
 *
 * Before `/auth/stores` answers, a scoped account still gets its shops (codes derived from
 * the numbers) so a per-shop link resolves immediately instead of waiting for names.
 */
export function insightShopsForUser(
  user: AuthUser | null,
  stores: StoreOption[] = [],
): InsightShop[] {
  if (!user) return []

  const catalogue: InsightShop[] = stores.map((store) => ({
    id: store.id,
    code: store.code,
    name: store.name ?? store.city ?? null,
  }))

  const scope = user.store_scope ?? user.scope_store_ids ?? []
  const companyWide =
    user.store_scope === null || user.is_platform_admin || COMPANY_WIDE_ROLES.has(user.role)

  if (companyWide) return catalogue

  if (scope.length > 0) {
    const scoped = catalogue.filter((store) => scope.includes(store.id))
    if (scoped.length > 0) return scoped
    return scope.map((id) => ({ id, code: storeCodeFromId(id), name: null }))
  }

  // An explicit empty scope means “no shops”, never “fall back to store_id”.
  if (user.store_scope !== undefined) return []

  if (user.store_id !== null && user.store_id !== undefined) {
    const own = catalogue.find((store) => store.id === user.store_id)
    return [own ?? { id: user.store_id, code: storeCodeFromId(user.store_id), name: null }]
  }

  return []
}

/**
 * True when the shop names/codes have to come from `GET /auth/stores`.
 *
 * Every screen labels a shop as `code · name`, so even a single-store account
 * needs the catalogue to resolve the name; only an anonymous visitor has nothing
 * to look up.
 */
export function needsShopList(user: AuthUser | null): boolean {
  return Boolean(user)
}

/** The shop to open first: the person's own store when it is in scope, else the first. */
export function defaultShopId(user: AuthUser | null, shops: InsightShop[]): number | null {
  if (shops.length === 0) return null
  if (
    user?.store_id !== null &&
    user?.store_id !== undefined &&
    shops.some((shop) => shop.id === user.store_id)
  ) {
    return user.store_id
  }
  return shops[0].id
}

/** Human label for a shop: `022 · Eindhoven Markt`, or just its code until the name is known. */
export function shopLabel(shop: InsightShop | null): string {
  if (!shop) return ''
  return shop.name ? `${shop.code} · ${shop.name}` : shop.code
}
